import { Model, Web3DViewer } from '@technology/web3d';
import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import { TcModelAlignment } from '@data-access/connect/tc-tcps';
import { Accessor, createEffect, createSignal, Setter } from 'solid-js';
import { getLogger } from '@trimble/logging';
import { SingleCapabilityProviderBase } from '../base/single-provider-base';
import { getTrbModelId, getTrbModelName } from './trim-bim-model-name-helpers';
import { convertConnectModelAlignmentToViewerTransform } from './placement-conversion';
import type { Matrix4 } from '@technology/web3d';

export class TrimBimCapabilityProvider extends SingleCapabilityProviderBase<Model> {
  public idKey = 'modelId' as const;

  public readonly isModelsVisible: Accessor<boolean>;
  /**
   * @intent `false` hides every loaded TrimBIM model of this viewer without
   * unloading it, and keeps models that finish loading later hidden too.
   * Hiding covers rendering and picking, so it writes entity visibility
   * through the geometry worker; that write is asynchronous and its failure
   * is logged, not thrown. A viewer that never sets `false` keeps full
   * control of entity visibility.
   */
  public readonly setIsModelsVisible: Setter<boolean>;

  /** Stays false until the first hide. See {@link setIsModelsVisible}. */
  private ownsModelVisibility = false;

  /** Serializes the worker writes, so a fast hide-then-show cannot land reversed. */
  private pendingVisibility: Promise<void> = Promise.resolve();

  constructor(viewer: Web3DViewer) {
    super(viewer);

    const [isModelsVisible, setIsModelsVisible] = createSignal<boolean>(true);
    this.isModelsVisible = isModelsVisible;
    this.setIsModelsVisible = setIsModelsVisible;

    createEffect(() => {
      const visible = this.isModelsVisible();
      // Tracked so a model that finishes loading while hidden does not appear.
      const models = this.items();

      if (visible && !this.ownsModelVisibility) return;
      this.ownsModelVisibility = !visible;

      const apply = (): Promise<void> =>
        this.applyModelVisibility(models, visible);
      this.pendingVisibility = this.pendingVisibility.then(apply, apply);
    });
  }

  private async applyModelVisibility(
    models: Model[],
    visible: boolean
  ): Promise<void> {
    for (const model of models) {
      // root.visible only affects rendering; TrimBIM picking runs in the
      // geometry worker against entity visibility, so the picker still hits a
      // hidden model until the entities go with it.
      model.root.visible = visible;

      if (!isTrimbimModel(model)) continue;

      try {
        await model.setVisibility(undefined, visible);
      } catch (error) {
        getLogger().error(
          `Failed to set visibility on TrimBIM model ${model.modelId}:`,
          error
        );
      }
    }
  }

  public async cleanup(): Promise<void> {
    await Promise.all(
      this.items().map((m) =>
        this.unloadModel(getTrbModelId(m[this.idKey]) ?? m[this.idKey])
      )
    );
  }

  public async loadModel(
    id: string,
    trimBim: ArrayBuffer,
    fitToView: boolean,
    /**
     * If true and the model is already loaded it will be unloaded before loading it again.
     * If false and the model is already loaded it will not be reloaded.
     */
    reloadModel: boolean = false,
    transform?: Matrix4
  ): Promise<Model | null> {
    const trbModelName = getTrbModelName(id);
    const currModel = this.viewer.getModel(trbModelName);
    if (currModel && !reloadModel) {
      return Promise.resolve(currModel);
    }

    if (this.isItemLoading(id)) {
      return null;
    }

    this.addToLoadingSet(id);

    try {
      if (currModel) {
        await this.viewer.unload(getTrbModelName(id), {
          fitToView: false,
        });
      }

      // Copying model with slice, otherwise trimbim plugin will crash
      // in case same model is loaded in multiple viewer instances.
      // TODO: optimize, don't copy it more than needed.
      // Also investigate use of `SharedArrayBuffer' (which is correct way to
      // use it according to Filipp). But Keep in mind that it has to be used in
      // "secure context":
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/SharedArrayBuffer#security_requirements
      const model = (await this.viewer.load(trimBim.slice(0), {
        modelId: getTrbModelName(id),
        fitToView: false,
        ...(transform && { transform }),
      })) as TrimbimModel;

      if (!this.addItem(model)) {
        throw new Error('TrimBIM model already added!');
      }

      if (fitToView) {
        await this.viewer.camera.fitToView(model.modelId);
      }

      return model;
    } finally {
      this.removeFromLoadingSet(id);
    }
  }

  public async unloadModel(id: Model[typeof this.idKey]): Promise<void> {
    const model = this.viewer.getModel(getTrbModelName(id));
    if (model != null) {
      await this.viewer.unload(getTrbModelName(id), {
        fitToView: false,
      });
      this.removeItem(model);
    }
    this.removeFromLoadingSet(id);
  }

  public applyModelAlignment(id: string, alignment: TcModelAlignment): void {
    const model = this.viewer.getModel<TrimbimModel>(getTrbModelName(id));
    if (!model) {
      return;
    }

    model.transform(convertConnectModelAlignmentToViewerTransform(alignment));
  }
}

const isTrimbimModel = (model: Model): model is TrimbimModel =>
  'setVisibility' in model && typeof model.setVisibility === 'function';
